import { AppType, UserAclOptions, UserStatus, UserType } from "./constants";
import { normalizeCarrierEmail } from "./carrierIdentity";

/**
 * Digits-only phone, mirroring driverApi's normalizePhone. CarrierUser.phone
 * feeds the exact-match carrierUserByPhone GSI the driver kiosk looks drivers
 * up by, so every form that writes the field must strip punctuation — a
 * stored "(555) 123-4567" is unfindable by the driver who types it.
 *
 * A NANP country code goes too: this form accepts "+1 (555) 123-4567" while
 * the driver at the gate types "555-123-4567", and an exact-match index makes
 * those two different people. 11 digits starting with 1 is the only ambiguous
 * shape in the NANP, so nothing shorter or longer is touched.
 *
 * Empty input stays undefined so react-admin drops the field.
 */
export const parsePhoneDigits = (v) => {
  if (!v) return undefined;
  const digits = String(v).replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

/**
 * Normalise a CarrierUserReloadYardCarrier `type` to a canonical UserType value.
 *
 * The junction's `type` is a plain String in the schema, not an enum, so rows
 * predating the CARRIER_* rename still carry the short forms "DISPATCH" and
 * "DRIVER". Those matched none of the branches that route a user to a portal,
 * which left carrier users with no portal and signed them out at login.
 * Normalising on read keeps such rows working without a data migration.
 *
 * "CARRIER_DISPATCH_ADMIN" folds into dispatch the same way. It was once a type
 * of its own, for the one account of a carrier that colleagues cannot delete;
 * that standing is now read from the addresses instead (see isCarrierAdmin), so
 * the stored value is a legacy spelling of plain dispatch. Folding it here is
 * what lets the Carrier Users dispatch tab — one begins_with prefix on the
 * reloadYardCarrierByActiveByTypeByName sort key — sort by business name rather
 * than group by role.
 *
 * A missing value (null/undefined/blank) keeps the long-standing CARRIER_DISPATCH
 * default. Any *other* unrecognised value is returned trimmed but unchanged
 * rather than folded into dispatch: the type now decides both the portal and the
 * ACL, so a typo, a corrupted row, or a carrier type a later build introduces
 * must not silently grant load claiming and user management. Callers gate on
 * isCarrierUserType before granting anything.
 *
 * @param {string} type - raw junction `type` value
 * @returns {string} a canonical UserType.CARRIER_* value, or the unrecognised
 *   value trimmed
 */
export const normalizeCarrierUserType = (type) => {
  const raw = String(type ?? "").trim();
  switch (raw.toUpperCase()) {
    case UserType.CARRIER_DRIVER:
    case "DRIVER":
      return UserType.CARRIER_DRIVER;
    case UserType.CARRIER_DISPATCH:
    case "CARRIER_DISPATCH_ADMIN":
    case "DISPATCH":
    case "":
      return UserType.CARRIER_DISPATCH;
    default:
      return raw;
  }
};

/**
 * Whether a normalised type is one this build knows how to admit to a portal.
 *
 * @param {string} type - a normalizeCarrierUserType result
 * @returns {boolean}
 */
export const isCarrierUserType = (type) =>
  type === UserType.CARRIER_DISPATCH || type === UserType.CARRIER_DRIVER;

/**
 * Whether a carrier user is a carrier's primary account.
 *
 * The company's own address is the primary account, so the answer is read from
 * the addresses rather than stored: the user is primary at a carrier when their
 * login address is the address on that carrier record. Nothing to migrate, and
 * nothing to keep in step — a carrier whose address changes hands moves the
 * standing with it, which is what "the company's address" has always meant.
 *
 * One primary membership anywhere makes the person a primary account someone
 * relies on. Inactive rows count on purpose: retiring the last active junction
 * is exactly the deletion this check exists to prevent, so a just-revoked
 * primary must not become deletable through the state the revocation created.
 *
 * The junction's denormalized `email` is not consulted — it is a copy of the
 * user's address made when the row was written, and a since-changed address
 * would answer for who the person used to be.
 *
 * @param {object} user - a CarrierUser record with its reloadYardCarriers, each
 *   carrying its `carrier`
 * @returns {boolean}
 */
export const isCarrierAdmin = (user) => {
  const userEmail = normalizeCarrierEmail(user?.email);
  if (!userEmail) return false;
  return (user?.reloadYardCarriers?.items ?? []).some((rycu) => {
    if (!rycu) return false;
    // A row naming a carrier whose record did not come back cannot be answered:
    // the query omitted the connection, or auth withheld it. This is the guard
    // against a colleague taking the company's own account over, so the
    // unanswerable case counts as protected — a delete button that should not
    // be there is a takeover, while one missing is a support call.
    if (rycu.carrierId && !rycu.carrier) return true;
    // Carriers created before the normalised column existed carry only the raw
    // address, so it is derived rather than required.
    const carrierEmail =
      normalizeCarrierEmail(rycu.carrier?.normalizedEmail) ||
      normalizeCarrierEmail(rycu.carrier?.email);
    return Boolean(carrierEmail) && carrierEmail === userEmail;
  });
};

/**
 * Whether the carrier's own people are the ones who maintain its profile.
 *
 * A carrier with a dispatch account holds its own business name, contacts and
 * regulatory numbers: the company knows its USDOT number and the yard does not,
 * and two yards editing the same company's details into two spellings is what
 * makes the records impossible to match up afterwards. So the manager's Carriers
 * form goes read-only from the moment there is somebody on the other side to
 * keep it — while archiving, restoring, deleting and inviting stay the yard's.
 *
 * Read from the junction rows rather than stored, for the same reason
 * isCarrierAdmin is: an invite is exactly what creates the row, a manager
 * removing the last one hands the record back, and a re-invite hands it over
 * again — with nothing to keep in step.
 *
 * Only live dispatch rows count. A driver enrolled at the gate has no portal to
 * edit anything from, so a carrier with drivers and no dispatcher would
 * otherwise lock a profile nobody could then maintain. A blocked or retired
 * dispatcher cannot sign in either (see getUsableCarrierJunctions), so neither
 * of those holds the record hostage.
 *
 * An archived carrier is always the yard's: setupCarrierData refuses the login
 * of every user of one, so there is no one else who could correct it.
 *
 * A truncated page is treated as self-managed. It means the answer cannot be
 * seen, and of the two mistakes, letting a yard overwrite a company's own
 * details is the one that loses data.
 *
 * @param {object} carrier - a Carrier record with its `users` connection
 * @returns {boolean}
 */
export const isCarrierProfileSelfManaged = (carrier) => {
  if (carrier?.active !== "true") return false;

  const rows = carrier?.users?.items ?? [];
  const hasLiveDispatch = rows.some(
    (row) =>
      row &&
      row.active === "true" &&
      row.blockAccess !== true &&
      normalizeCarrierUserType(row.type) === UserType.CARRIER_DISPATCH,
  );

  if (hasLiveDispatch) return true;
  return Boolean(carrier?.users?.nextToken);
};

/**
 * Whether this person already has a login, and so which invitation to send.
 *
 * An invite is one email with two possible next steps — register an account, or
 * sign in to the one you have — and sending the wrong one strands the invitee
 * on a page that cannot help them. The pool takes the email address as the
 * username, so somebody told to register when they already have an account is
 * refused for an address that is their own, and somebody told to sign in when
 * they have no account has no password to sign in with.
 *
 * Both signals must agree before this says no login, because they fail in
 * opposite directions. Deleting a carrier user's last membership deletes the
 * Cognito account and clears cognitoId with it (handleCarrierUserChange), so a
 * cleared id is the reliable mark of a purge — but rows predating that write
 * never carried an id in the first place, and reading only the id would send
 * long-registered people to a sign-up they cannot complete.
 *
 * @param {object} carrierUser - a CarrierUser record with status and cognitoId
 * @returns {boolean} true when the person can sign in today
 */
export const hasCarrierLogin = (carrierUser) => {
  if (!carrierUser) return false;
  if (carrierUser.cognitoId) return true;
  return carrierUser.status !== UserStatus.PENDING_REG;
};

/**
 * Whether a getUserByEmail result is a carrier user, decided structurally.
 *
 * Login routing must not key off the junction `type`. getUserByEmail can only
 * offer a *provisional* type read from reloadYardCarriers.items[0] — an
 * arbitrary junction, since the relation is unordered — so an unrecognised
 * value there matched none of hydrate()'s branches and signed the user out.
 * That bypassed setupCarrierData, which is what picks the right junction (by
 * stored reloadYardId, filtered to active ones) and renders
 * CarrierBlockedScreen when the selected junction's type really is unusable.
 * The same provisional type also chose updateUser's mutation, aiming a carrier
 * user's id at the User table.
 *
 * Only the carrier query selects reloadYardCarriers — yard users carry
 * reloadYardId, customers carry reloadYardAccounts — so its presence identifies
 * the record no matter what any junction holds. An empty items array still
 * counts: such a user belongs in setupCarrierData's blocked branch, not signed
 * out.
 *
 * @param {object} user - a getUserByEmail result
 * @returns {boolean}
 */
export const isCarrierUserRecord = (user) => Boolean(user?.reloadYardCarriers);

/**
 * Split a carrier user's junctions into the ones that can carry them into a
 * portal and the ones whose type this build cannot route.
 *
 * A carrier user has one junction per yard they work with, so every reason a
 * junction is unusable is a reason to skip *that yard*, not to deny the user a
 * portal outright.
 *
 * @param {object[]} carrierUsers - the user's CarrierUserReloadYardCarrier rows
 * @returns {{usable: object[], unusableTypes: object[]}}
 */
export const getUsableCarrierJunctions = (carrierUsers) => {
  // active="false" covers both deleted (deleted=true) and archived-with-carrier
  // users — archive cascades active="false" to all junction records, so no
  // separate carrier.active check is needed here. blockAccess excludes suspended
  // users. Neither is worth reporting: both are a yard's own decision.
  const accessible = (carrierUsers ?? []).filter(
    (rycu) => rycu?.active === "true" && !rycu.blockAccess,
  );

  // The ACL is derived from the type now, so treating an unknown as dispatch
  // would hand it load claiming and user management; before that derivation such
  // a row simply had no portal to route to, and it stays that way.
  const usable = [];
  const unusableTypes = [];
  accessible.forEach((rycu) => {
    if (isCarrierUserType(normalizeCarrierUserType(rycu.type)))
      usable.push(rycu);
    else unusableTypes.push(rycu);
  });

  return { usable, unusableTypes };
};

/**
 * Choose which junction a carrier user signs in against.
 *
 * Unusable junctions are filtered out before anything is picked, so a bad row
 * can never be the one selected — whether it happens to be first or happens to
 * be the one the last session left in localStorage. A user with nothing usable
 * anywhere gets no junction, and the caller's blocked screen.
 *
 * Two junctions can share the stored yard — two carrier entries at one yard,
 * both having invited this person — so the yard alone does not name the session
 * to resume; the stored carrier breaks the tie. Sessions stored before the
 * carrier was written, or whose carrier has since lost the yard, fall back to
 * the yard's first usable junction, and a stored yard that matches nothing
 * falls back to any usable junction: at login some portal is better than none.
 *
 * The unusable-type rows are handed back rather than logged here: they are worth
 * reporting, and keeping this function pure keeps it testable.
 *
 * @param {object[]} carrierUsers - the user's CarrierUserReloadYardCarrier rows
 * @param {string} [storedReloadYardId] - the yard the last session left behind
 * @param {string} [storedCarrierId] - the carrier that session represented
 * @returns {{reloadYardCarrier: object|null, unusableTypes: object[]}}
 */
export const selectCarrierJunction = (
  carrierUsers,
  storedReloadYardId,
  storedCarrierId,
) => {
  const { usable, unusableTypes } = getUsableCarrierJunctions(carrierUsers);
  const atStoredYard = usable.filter(
    (rycu) => rycu.reloadYardId === storedReloadYardId,
  );

  return {
    reloadYardCarrier:
      atStoredYard.find((rycu) => rycu.carrierId === storedCarrierId) ??
      atStoredYard[0] ??
      usable[0] ??
      null,
    unusableTypes,
  };
};

/**
 * The permissions a carrier user gets, derived purely from their type.
 *
 * Carrier permissions are no longer editable per user: every dispatch user needs
 * User Management and Claim Loads to do the job, so the checkboxes were only
 * ever noise. Deriving the list from the type — rather than reading the
 * junction's stored `acl` — also grants those rights to rows created before the
 * inputs were removed, without a data migration.
 *
 * @param {string} type - raw or canonical junction `type` value
 * @returns {string[]} AclType ids granted to that type
 */
export const getCarrierUserAcl = (type) => {
  const carrierUserType = normalizeCarrierUserType(type);
  // An unrecognised type grants nothing. Spelled out rather than left to the
  // filter finding no match, so it stays true if a UserAclOptions row is ever
  // given a wildcard-ish role.
  if (!isCarrierUserType(carrierUserType)) return [];
  return UserAclOptions.filter((acl) => acl.roles[carrierUserType]).map(
    (acl) => acl.id,
  );
};

/** Which portal a canonical carrier type belongs in. */
const getCarrierAppType = (carrierUserType) =>
  carrierUserType === UserType.CARRIER_DRIVER
    ? AppType.DRIVER
    : AppType.CARRIER;

/**
 * Everything about a session that the chosen junction decides.
 *
 * A carrier user holds one junction per yard, so signing in and switching yards
 * are the same operation applied to a different row — and both have to derive
 * the same fields from it, or the portal ends up showing one yard's data under
 * another yard's permissions. Sharing the derivation is what keeps them honest.
 *
 * `type` is re-derived rather than trusted: getUserByEmail can only set a
 * provisional one from an arbitrary junction, and legacy rows store the
 * pre-rename short forms. `permissionList` comes from that type rather than the
 * junction's stored `acl` — carrier permissions are no longer per-user, so every
 * dispatch user gets the full set regardless of what was checked when their
 * record was created.
 *
 * @param {object} reloadYardCarrier - the selected CarrierUserReloadYardCarrier
 * @returns {object} fields to spread over the app user
 */
export const buildCarrierSession = (reloadYardCarrier) => {
  const type = normalizeCarrierUserType(reloadYardCarrier?.type);

  const permissionList = {};
  getCarrierUserAcl(type).forEach((acl) => {
    permissionList[acl] = true;
  });

  return {
    reloadYardId: reloadYardCarrier?.reloadYardId,
    carrierId: reloadYardCarrier?.carrierId,
    reloadYardCarrier,
    permissionList,
    type,
    appType: getCarrierAppType(type),
  };
};

/**
 * One carrier at one yard — the pair a junction row actually stands for.
 *
 * A Carrier record is already per yard, but two records can share a yard — two
 * companies' entries at it, both having invited the same person — so neither
 * half identifies a membership on its own.
 *
 * @param {object} junction - anything carrying reloadYardId and carrierId
 * @returns {string}
 */
export const carrierMembershipKey = (junction) =>
  `${junction?.reloadYardId}::${junction?.carrierId}`;

/**
 * A person's membership in one carrier entry, if they hold one.
 *
 * Both halves of the key are needed, and a match on the yard alone is what this
 * exists to stop: a carrier user working three yards holds three rows, so taking
 * the first row an unordered relation returns answers for whichever yard came
 * back — reading "already a member here" off a membership somewhere else.
 *
 * Retired rows count. A membership that was removed is the row a re-invite has
 * to revive rather than duplicate, so the caller has to see it.
 *
 * @param {object[]} junctions - the person's junction rows
 * @param {object} membership - anything carrying reloadYardId and carrierId
 * @returns {object|undefined}
 */
export const findCarrierMembership = (junctions, membership) =>
  (junctions ?? []).find(
    (junction) =>
      junction &&
      carrierMembershipKey(junction) === carrierMembershipKey(membership),
  );

/**
 * One company, as far as two carrier entries can be compared.
 *
 * Kept identical to companyKey in the junction stream's colleagueCascade.js,
 * which decides the same question about the colleagues it carries over: the
 * entries are separate records with separate ids, so the business name is all
 * they share — spelling, case and spacing aside.
 *
 * @param {string} businessName
 * @returns {string} the name to compare on, empty if there is none
 */
export const carrierCompanyKey = (businessName) =>
  String(businessName ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * The acting user's memberships that belong to the company they are acting for.
 *
 * A dispatcher's junctions can, rarely, span two companies — the person one yard
 * invited may dispatch for somebody else at another — and an invite is one
 * company taking somebody on: the colleague joins the transloads *this* company
 * works, never the other one's. Without this filter the invite reads the pool as
 * "every yard I can reach" and writes the new user a membership under the other
 * company's entry, handing them its loads and contacts. The junction stream
 * refuses exactly that cascade (planSourceEntryIds), so an unscoped list here
 * would grant by hand what the backend will not grant by rule.
 *
 * A session junction carrying no name to compare falls back to itself rather
 * than to everything: the fallback has to narrow, since falling back to every
 * membership is the cross-company grant this exists to prevent.
 *
 * Only the invite paths want this. A yard switcher must keep offering the other
 * company's yards — they are the user's own — so getCarrierYardOptions stays
 * company-blind.
 *
 * @param {object[]} carrierUsers - the acting user's junction rows
 * @param {object} session - the standing being acted from
 * @param {string} [session.businessName] - the session junction's business name
 * @param {string} [session.carrierId] - the entry the session was built from
 * @returns {object[]} the rows under that company
 */
export const carrierCompanyJunctions = (carrierUsers, session = {}) => {
  const company = carrierCompanyKey(session.businessName);

  return (carrierUsers ?? []).filter((junction) =>
    company
      ? carrierCompanyKey(junction?.businessName) === company
      : Boolean(session.carrierId) && junction?.carrierId === session.carrierId,
  );
};

/*
 * A note on carrier scope, for every helper below.
 *
 * A Carrier record is one company's entry at ONE yard (Carrier.reloadYardId),
 * so a person working three yards for one company holds three junctions
 * carrying three different carrierIds — and no field anywhere links those
 * entries into "the company". The closest thing the data has to a user's
 * carrier is their junction set itself, and that is the scope these helpers
 * work in.
 *
 * An earlier revision narrowed them to the session's carrierId instead, which
 * read as "the represented company" but actually named one company-at-one-yard
 * — collapsing every multi-yard screen to the yard in session. A carrierId
 * argument here is a tie-break for the yard shared by two entries, never a
 * filter.
 */

/**
 * The memberships an acting user stands in, as a set of keys to test against.
 *
 * @param {object[]} actorJunctions - the acting user's junction rows
 * @returns {Set<string>} carrierMembershipKey values
 */
export const carrierMembershipScope = (actorJunctions) =>
  new Set(
    getUsableCarrierJunctions(actorJunctions).usable.map(carrierMembershipKey),
  );

/**
 * What inviting someone to a carrier's transloads has to write.
 *
 * A carrier dispatch user invites a colleague into every transload the company
 * works with at once, and each one is a separate junction row carrying that
 * yard's carrier and groups. Those come from the inviter's own row at the yard
 * rather than from the form: it is the record that says what standing the
 * inviter has there, so a new user can never be given access the person
 * inviting them does not have.
 *
 * An invitee who already has an account is common — any yard can invite the
 * same address, and one junction per yard is how a single account reaches
 * several of them — so a yard they already belong to is reported rather than
 * written twice, and a membership that was removed is revived in place rather
 * than duplicated. Reviving carries the row's own blockAccess with it, which is
 * what makes a yard's block survive being removed and re-invited by a
 * colleague: the membership comes back, the access does not.
 *
 * @param {object[]} inviterJunctions - the inviting user's junction rows
 * @param {object[]} inviteeJunctions - the invited user's rows, empty if new
 * @param {string[]} reloadYardIds - the yards to grant
 * @param {object} [options]
 * @param {string} [options.carrierId] - the carrier entry the session was built
 *   from; a tie-break for the yard where the inviter holds two entries, not a
 *   filter
 * @returns {{create: object[], reactivate: object[], alreadyMember: string[],
 *   unavailable: string[]}} yard fields to write, rows to revive, and the yards
 *   that need neither
 */
export const planCarrierUserYards = (
  inviterJunctions,
  inviteeJunctions,
  reloadYardIds,
  options = {},
) => {
  const { carrierId } = options;
  const pool = getUsableCarrierJunctions(inviterJunctions).usable;
  const plan = {
    create: [],
    reactivate: [],
    alreadyMember: [],
    unavailable: [],
  };

  (reloadYardIds ?? []).forEach((reloadYardId) => {
    const candidates = pool.filter(
      (junction) => junction.reloadYardId === reloadYardId,
    );
    // Almost always one row: a junction is one person, one yard, one carrier
    // entry. Where two entries at the yard have both invited the inviter, the
    // entry the session was built from is the one its portal acts for.
    const inviterJunction =
      candidates.find((junction) => junction.carrierId === carrierId) ??
      candidates[0];
    // The form offers only yards the inviter stands in, so this is a stale form
    // or a yard their access to has since been withdrawn. Either way it is not
    // theirs to grant.
    if (!inviterJunction) {
      plan.unavailable.push(reloadYardId);
      return;
    }

    const yardFields = {
      reloadYardId,
      carrierId: inviterJunction.carrierId,
      businessName: inviterJunction.businessName,
      groups: inviterJunction.groups,
    };

    const existing = (inviteeJunctions ?? []).find(
      (junction) =>
        carrierMembershipKey(junction) === carrierMembershipKey(yardFields),
    );

    if (!existing) plan.create.push(yardFields);
    else if (existing.active === "true") plan.alreadyMember.push(reloadYardId);
    // deleted is cleared alongside active: a row retired by a delete has both
    // set, and reviving it without clearing deleted leaves it invisible to the
    // paths that treat the flag as final.
    else
      plan.reactivate.push({
        ...existing,
        ...yardFields,
        active: "true",
        deleted: false,
      });
  });

  return plan;
};

/**
 * The junctions of one carrier user that another may administer.
 *
 * A dispatch user administers the memberships they share — the same carrier
 * entries, at the yards they themselves work with — not the other person's
 * whole account. Access held at a yard the actor has no standing in, or under
 * a different company's entry at a shared yard, keeps: it was never this
 * user's to grant and is not theirs to take.
 *
 * The same set answers both questions the profile asks: which memberships show
 * as granted, and which ones a removal may retire.
 *
 * @param {object[]} targetJunctions - the user being administered
 * @param {object[]} actorJunctions - the user doing the administering
 * @returns {object[]} the target's active rows inside the actor's standing
 */
export const administeredCarrierJunctions = (
  targetJunctions,
  actorJunctions,
) => {
  const actorScope = carrierMembershipScope(actorJunctions);

  return (targetJunctions ?? []).filter(
    (junction) =>
      junction?.active === "true" &&
      actorScope.has(carrierMembershipKey(junction)),
  );
};

/**
 * Name a yard from the acting user's own junctions.
 *
 * A colleague's junctions carry no reloadYard — getCarrierUser does not select
 * one — but nothing is said about a yard the acting user has no standing in, and
 * theirs are named.
 *
 * @param {string} reloadYardId
 * @param {object[]} actorJunctions - the acting user's junction rows
 * @returns {string|undefined} the yard's name, if it is one of theirs
 */
export const carrierYardName = (reloadYardId, actorJunctions) =>
  (actorJunctions ?? []).find(
    (junction) => junction?.reloadYardId === reloadYardId,
  )?.reloadYard?.name;

/**
 * Where a carrier user stands at each transload, for a profile to report.
 *
 * Carrier users are not given their access one transload at a time any more:
 * joining the company grants every transload it works with, and taking one away
 * is the transload's own decision — its manager blocks the user there, or
 * removes them. So a profile has nothing to ask and something to say, which is
 * what this answers: for each transload the acting user works with, whether this
 * person can work it, is blocked at it, or holds no membership in it.
 *
 * The transloads come from the acting user's own rows because those are the ones
 * carrying a reloadYard to name (getCarrierUser selects none for the person being
 * looked at) — and a yard the actor has no live standing in is not theirs to
 * report on. Blocked rows of the actor's own stay in the list: a dispatcher
 * blocked somewhere is being told so on their own profile, not shown a yard
 * short.
 *
 * Both halves of the membership key matter, so the carrier entry comes from the
 * actor's row at each yard — the session's entry where two of them share a yard.
 * A person's row under some other company's entry is another membership and is
 * not this one's answer.
 *
 * @param {object[]} targetJunctions - the person being looked at
 * @param {object[]} actorJunctions - the acting user's junction rows
 * @param {string} appType - the portal doing the asking, an AppType value
 * @param {string} [carrierId] - the carrier entry the session was built from
 * @returns {{reloadYardId: string, name: string, state: string}[]} one row per
 *   transload, state one of "active" | "blocked" | "none"
 */
export const carrierYardAccessRows = (
  targetJunctions,
  actorJunctions,
  appType,
  carrierId,
) => {
  const seen = new Set();
  const rows = [];

  (actorJunctions ?? []).forEach((actorJunction) => {
    const reloadYard = actorJunction?.reloadYard;
    if (
      actorJunction?.active !== "true" ||
      !reloadYard?.id ||
      !reloadYard?.name
    )
      return;
    const actorType = normalizeCarrierUserType(actorJunction.type);
    if (!isCarrierUserType(actorType)) return;
    if (getCarrierAppType(actorType) !== appType) return;
    if (seen.has(reloadYard.id)) return;

    // The yard's other entry, where the actor holds one there too, is a
    // different company and a different question.
    const atYard = (actorJunctions ?? []).filter(
      (junction) =>
        junction?.active === "true" && junction.reloadYardId === reloadYard.id,
    );
    const entry =
      atYard.find((junction) => junction.carrierId === carrierId) ?? atYard[0];

    seen.add(reloadYard.id);
    const membership = findCarrierMembership(targetJunctions, {
      reloadYardId: reloadYard.id,
      carrierId: entry?.carrierId,
    });
    const state =
      membership?.active !== "true"
        ? "none"
        : membership.blockAccess
          ? "blocked"
          : "active";

    rows.push({ reloadYardId: reloadYard.id, name: reloadYard.name, state });
  });

  return rows;
};

/**
 * The yards a carrier user's switcher may offer, for the portal they are in.
 *
 * Two filters, for two different reasons:
 *
 * Unusable junctions are dropped because login would have skipped them — the
 * switcher must not be a way into a yard the sign-in path refuses.
 *
 * Junctions belonging to another portal are dropped because selecting one would
 * eject the user out of the portal the switcher lives in, and persist that yard
 * as the one their next sign-in resumes. A dispatch user who also drives for a
 * second yard would land in the driver app with no way back to dispatch.
 *
 * A junction whose `reloadYard` the caller's query did not select is dropped
 * too: with no name there is nothing to put in the menu, and switching into an
 * unidentifiable yard is worse than not offering it. Two junctions can name the
 * same yard — two carrier entries there, one person — so the list is
 * deduplicated on yard id, keeping the first, which is the junction
 * selectCarrierJunction would pick.
 *
 * The count can therefore come out lower than isMultiTenant's, which counts
 * every usable junction. That only costs a switcher with one entry in it.
 *
 * @param {object[]} carrierUsers - the user's CarrierUserReloadYardCarrier rows
 * @param {string} appType - the portal doing the asking, an AppType value
 * @returns {object[]} the junctions' reloadYard objects, deduplicated
 */
export const getCarrierYardOptions = (carrierUsers, appType) => {
  const pool = getUsableCarrierJunctions(carrierUsers).usable;
  const seen = new Set();
  return pool
    .filter(
      (rycu) =>
        getCarrierAppType(normalizeCarrierUserType(rycu.type)) === appType,
    )
    .map((rycu) => rycu.reloadYard)
    .filter((reloadYard) => {
      if (!reloadYard?.id || !reloadYard?.name) return false;
      if (seen.has(reloadYard.id)) return false;
      seen.add(reloadYard.id);
      return true;
    });
};

/**
 * Resolve the junction a yard switcher's choice stands for.
 *
 * The switcher's menu names yards, but a session is built from a junction, and
 * a person can hold two at the chosen yard — one per carrier entry there.
 * Matching on the yard alone took whichever the unordered relation returned
 * first, which could rebuild the session under the yard's other entry — or,
 * if that junction belongs to the other portal, eject a dispatch user into
 * the driver app. So the candidates are getCarrierYardOptions' own: usable,
 * and routable by the portal doing the asking. Among those the entry the
 * session was built from wins, and any other yard resolves to its own row —
 * a different carrierId by necessity, since every yard keeps its own Carrier
 * records.
 *
 * Strict where selectCarrierJunction is lenient: falling back to some other
 * yard is the right answer when the alternative is no portal at all, and the
 * wrong one when the user named the yard they want. Nothing here means the
 * caller reports it.
 *
 * @param {object[]} carrierUsers - the user's CarrierUserReloadYardCarrier rows
 * @param {string} reloadYardId - the yard picked from the switcher
 * @param {string} appType - the portal doing the asking, an AppType value
 * @param {string} [carrierId] - the carrier the session represents
 * @returns {object|null} the junction to build the next session from
 */
export const selectCarrierJunctionForYard = (
  carrierUsers,
  reloadYardId,
  appType,
  carrierId,
) => {
  const candidates = getUsableCarrierJunctions(carrierUsers).usable.filter(
    (rycu) =>
      rycu.reloadYardId === reloadYardId &&
      getCarrierAppType(normalizeCarrierUserType(rycu.type)) === appType,
  );

  return (
    candidates.find((rycu) => rycu.carrierId === carrierId) ??
    candidates[0] ??
    null
  );
};

/**
 * Whether a gate-enrolled driver's name disagrees with the name read off their
 * ID document, per half of the name.
 *
 * Both halves are re-derived on every read. driverApi's parseIdDoc stores what
 * the document said — the OCR'd last name, and the INITIAL of the OCR'd first
 * name (the first name itself is never stored) — and the comparison against the
 * driver's current name happens here. A rename after enrollment therefore
 * answers correctly on the next render, in both directions: a name edited away
 * from the document starts flagging, one corrected to match stops.
 *
 * This replaced a scan-time `idDocNameMismatch` boolean, which went stale on
 * exactly that rename and had no honest way to be reset — clearing it on the
 * edit would have let the edit erase the fake-name signal it exists to raise.
 * Rows enrolled before the change carry no first initial and simply compare on
 * the last name.
 *
 * A half the document never yielded is not evidence of anything, and neither is
 * a blank name on the record, so both stay silent.
 */
const comparableIdentity = (identity) =>
  identity && identity.status !== "PURGED" ? identity : null;

const normalizeName = (value) => String(value || "").trim().toLowerCase();

export const hasIdDocLastNameMismatch = (record, identity) => {
  const idDocLastName = comparableIdentity(identity)?.idDocLastName;
  if (!idDocLastName) return false;
  return normalizeName(idDocLastName) !== normalizeName(record?.lastName);
};

/**
 * Initial against initial, never the whole first name: "Mike" on the record and
 * "Michael" on the license is the same driver, and flagging it would flag half
 * the fleet.
 */
export const hasIdDocFirstNameMismatch = (record, identity) => {
  const idDocFirstInitial = comparableIdentity(identity)?.idDocFirstInitial;
  if (!idDocFirstInitial) return false;
  const recordInitial = normalizeName(record?.firstName).charAt(0);
  if (!recordInitial) return false;
  return normalizeName(idDocFirstInitial).charAt(0) !== recordInitial;
};

export const hasIdNameMismatch = (record, identity) =>
  hasIdDocLastNameMismatch(record, identity) ||
  hasIdDocFirstNameMismatch(record, identity);
